/**
 * Table-specific diff algorithm.
 *
 * An exact ordered tree diff over the HTML table subtree.  Deterministic
 * but not yet a logical grid diff (merge/split semantics are future work).
 *
 * Good enough for:
 * - Row insertion / deletion
 * - Matched cell content diffs
 * - th ↔ td type changes
 * - Explicit rowspan / colspan attribute diffs
 */
import type { Element } from 'domhandler';

import { TABLE_WRAPPER_TAGS, TABLE_CELL_TAGS, TABLE_ROW_TAG, getElementId, getAttrs } from '../schema';
import { attrsEqual } from './compare';
import { diffSequence, getElementChildren, elementsMatch } from './matcher';
import type { TDiffNode } from '../types';

/**
 * Legacy row signature - enables matching rows that lack data-id but whose
 * cells all have ids.  The signature is the pipe-joined list of cell tag#id
 * strings.  Returns null if any cell lacks an id.
 */
const getLegacyRowSignature = (el: Element): string | null => {
  if (el.tagName !== TABLE_ROW_TAG) return null;

  const cellSignatures = getElementChildren(el).map((cell) => {
    const cellId = getElementId(cell);
    return cellId ? `${cell.tagName}#${cellId}` : null;
  });

  if (cellSignatures.length === 0 || cellSignatures.some((sig) => !sig)) return null;

  return cellSignatures.join('|');
};

export const diffTableNode = (
  curr: Element,
  prev: Element,
  diffNode: (curr: Element, prev: Element) => TDiffNode,
  buildInserted: (el: Element) => TDiffNode,
  buildDeleted: (el: Element) => TDiffNode
): TDiffNode => {
  const currChildren = getElementChildren(curr);
  const prevChildren = getElementChildren(prev);

  /**
   * Table-specific equality:
   * 1. If either side carries a data-id, identity is decided by that id.
   * 2. th and td may match across type change if they share the same id.
   * 3. Wrapper nodes (tbody, thead) match by type alone.
   * 4. Rows without ids fall back to legacy signature matching.
   */
  const operations = diffSequence(prevChildren, currChildren, (prevChild, currChild) => {
    const prevId = getElementId(prevChild);
    const currId = getElementId(currChild);

    if (prevId || currId) {
      if (prevId !== currId) return false;
      if (prevChild.tagName === currChild.tagName) return true;
      return TABLE_CELL_TAGS.has(prevChild.tagName) && TABLE_CELL_TAGS.has(currChild.tagName);
    }

    const prevLegacy = getLegacyRowSignature(prevChild);
    const currLegacy = getLegacyRowSignature(currChild);
    if (prevLegacy || currLegacy) {
      return prevLegacy != null && prevLegacy === currLegacy;
    }

    if (prevChild.tagName === currChild.tagName && TABLE_WRAPPER_TAGS.has(prevChild.tagName)) {
      return true;
    }

    return elementsMatch(prevChild, currChild);
  });

  const children: TDiffNode[] = [];

  operations.forEach((operation) => {
    if (operation.type === 'equal') {
      children.push(diffNode(operation.currItem, operation.prevItem));
      return;
    }
    if (operation.type === 'insert') {
      children.push(buildInserted(operation.currItem));
      return;
    }
    children.push(buildDeleted(operation.prevItem));
  });

  const attrsChanged = !attrsEqual(curr.tagName, getAttrs(curr), getAttrs(prev));
  const typeChanged = curr.tagName !== prev.tagName;
  const hasChildChanges = children.some((child) => child.change !== 'unchanged');

  return {
    type: curr.tagName,
    change: attrsChanged || typeChanged || hasChildChanges ? 'modified' : 'unchanged',
    attrs: getAttrs(curr),
    prevAttrs: attrsChanged ? getAttrs(prev) : undefined,
    marks: [],
    children,
    currElement: curr,
    prevElement: prev,
  };
};
